// UnifiedDetailedBalanceSheet.tsx
import React, { useState, useEffect, useCallback, forwardRef } from 'react';
import { ChevronDown, ChevronRight, ChevronDownSquare, ChevronUpSquare } from 'lucide-react';
import { AccountEntry, AccountType, CategoryTemplate, Project, ViewType } from '../../types';
import { ViewToggle } from '../ui/ViewToggle';
import { compareMonthYear as compareMonthYearHelper, getAllFinancialReports } from '../../lib/data/financialReportsHelper';
import { getDateForPeriod } from '../../lib/utils/dateMapping';
import { getSupabase } from '../../lib/api/supabase';
import { FrontendBSCalculationService } from '../../lib/financial/frontendBSCalculations';

interface UnifiedDetailedBalanceSheetProps {
  accounts: AccountEntry[];
  activeTemplate: CategoryTemplate | null;
  selectedProject?: Project | null;
}

// ViewType now imported from types.ts

interface BSLineItem {
  type: 'section_header' | 'category_header' | 'account' | 'category_total' | 'section_total' | 'major_total' | 'calculated_total' | 'balance_check';
  name: string;
  values?: number[];
  level: number;
  section_id?: string;
  category_value?: string;
  account_number?: string;
  category?: string;
  is_check?: boolean;
}

interface BSDetailData {
  periods: string[];
  line_items: BSLineItem[];
}

interface BalanceSheetSection {
  id: string;
  title: string;
  types: AccountType[];
  categories?: string[];
}

// UPDATE the component declaration to use forwardRef
export const UnifiedDetailedBalanceSheet = forwardRef<void, UnifiedDetailedBalanceSheetProps>(({ 
  accounts, 
  activeTemplate, 
  selectedProject 
}, ref) => {
  // Initialize viewType based on project period type if available
  const getInitialViewType = (): ViewType => {
    if (selectedProject?.period_type === 'annual') return 'yearly';
    if (selectedProject?.period_type === 'quarterly') return 'quarterly';
    return 'monthly';
  };

  const [viewType, setViewType] = useState<ViewType>(getInitialViewType());
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [bsDetailData, setBSDetailData] = useState<BSDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingExpandAll, setPendingExpandAll] = useState(false);

  // Frontend calculation mode - use frontend calculations by default
  const [calculationSource, setCalculationSource] = useState<'frontend' | 'backend'>('frontend');

  // Generate frontend BS data
  const frontendBSData = React.useMemo(() => {
    if (calculationSource === 'frontend' && accounts.length > 0 && activeTemplate) {
      try {
        return FrontendBSCalculationService.buildBSData(accounts, activeTemplate, viewType, selectedProject);
      } catch (err) {
        console.error('Error generating frontend BS data:', err);
        return null;
      }
    }
    return null;
  }, [calculationSource, accounts, activeTemplate, viewType, selectedProject]);

  // Define balance sheet sections with their account types
  const sections: BalanceSheetSection[] = [
    {
      id: 'current-assets',
      title: 'Current Assets',
      types: ['current-asset', 'contra-current-asset']
    },
    {
      id: 'non-current-assets',
      title: 'Non-current Assets',
      types: ['non-current-asset', 'contra-non-current-asset']
    },
    {
      id: 'current-liabilities',
      title: 'Current Liabilities',
      types: ['current-liability', 'contra-current-liability']
    },
    {
      id: 'non-current-liabilities',
      title: 'Non-current Liabilities',
      types: ['non-current-liability', 'contra-non-current-liability']
    },
    {
      id: 'equity',
      title: 'Equity',
      types: ['equity', 'contra-equity']
    }
  ];

  // Get all months and sort them
  const allMonths = [...new Set(accounts.map(a => a.month))]
    .sort(compareMonthYearHelper);

  // Function to determine if a month is a quarter end
  const isQuarterEnd = (month: string) => {
    const monthStr = month.split('-')[0];
    return ['Mar', 'Jun', 'Sep', 'Dec'].includes(monthStr);
  };

  // Function to determine if a month is December
  const isDecember = (month: string) => {
    return month.split('-')[0] === 'Dec';
  };

  // Function to get month range for a given year
  const getYearMonthRange = (month: string) => {
    if (isDecember(month)) return null;
    return month.split('-')[0];
  };

  // Get displayed periods based on view type
  // getDisplayedPeriods update to use compareMonthYear for consistency
  const getDisplayedPeriods = () => {
    const mostRecentMonth = allMonths[allMonths.length - 1];

    if (viewType === 'monthly') {
      return allMonths;
    } else if (viewType === 'quarterly') {
      const quarterEndMonths = allMonths.filter(isQuarterEnd);
      if (!isQuarterEnd(mostRecentMonth)) {
        quarterEndMonths.push(mostRecentMonth);
      }
      return quarterEndMonths.sort(compareMonthYearHelper);
    } else {
      const decemberMonths = allMonths.filter(isDecember);
      if (!isDecember(mostRecentMonth)) {
        decemberMonths.push(mostRecentMonth);
      }
      return decemberMonths.sort(compareMonthYearHelper);
    }
  };

  const months = getDisplayedPeriods();

  // Load BS data from backend (only when using backend calculations or as fallback)
  useEffect(() => {
    const loadBSData = async () => {
      // Only load backend data if using backend calculations or if frontend data is not available
      if (!selectedProject?.id || (calculationSource === 'frontend' && frontendBSData)) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const reports = await getAllFinancialReports(selectedProject.id);

        if (!reports.bs_detail?.data) {
          // If frontend data is available, don't show error
          if (calculationSource === 'frontend' && frontendBSData) {
            setLoading(false);
            return;
          }
          setError('Balance Sheet detail report not found. Please generate reports first.');
          return;
        }

        setBSDetailData(reports.bs_detail.data);

      } catch (err) {
        console.error('Error loading BS data:', err);
        // If frontend data is available, don't show error
        if (calculationSource === 'frontend' && frontendBSData) {
          setLoading(false);
          return;
        }
        setError('Failed to load Balance Sheet data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadBSData();
  }, [selectedProject?.id, calculationSource, frontendBSData]);

  // Auto-select appropriate view based on project period type
  React.useEffect(() => {
    if (selectedProject?.period_type) {
      const periodType = selectedProject.period_type;
      const availableViews: ViewType[] =
        periodType === 'quarterly' ? ['quarterly', 'yearly'] :
        periodType === 'annual' ? ['yearly'] :
        ['monthly', 'quarterly', 'yearly'];

      // If current view is not available for this period type, switch to first available
      if (!availableViews.includes(viewType)) {
        setViewType(availableViews[0]);
      }
    }
  }, [selectedProject?.period_type]);

  // Get data source based on calculation mode
  const currentDataSource = calculationSource === 'frontend' && frontendBSData
    ? frontendBSData
    : bsDetailData;

  // Auto-expand all after data loads if pendingExpandAll is set
  useEffect(() => {
    if (pendingExpandAll && currentDataSource) {
      expandAll();
    }
  }, [pendingExpandAll, currentDataSource]);

  // Set all categories collapsed on data load
  useEffect(() => {
    if (currentDataSource) {
      const newCategories: Record<string, boolean> = {};
      currentDataSource.line_items
        .filter(item => item.type === 'category_header' && item.category_value)
        .forEach(item => {
          if (item.category_value) {
            newCategories[item.category_value] = false;
          }
        });
      setExpandedCategories(newCategories);
      // Expand all sections by default
      const newSections: Record<string, boolean> = {};
      currentDataSource.line_items
        .filter(item => item.type === 'section_header' && item.section_id)
        .forEach(item => {
          if (item.section_id) {
            newSections[item.section_id] = true;
          }
        });
      setExpandedSections(newSections);
    }
  }, [currentDataSource]);

  // Transform data for view type
  const transformDataForView = useCallback((data: BSDetailData | null): BSDetailData | null => {
    if (!data) return null;
    if (viewType === 'monthly') return data;
    const monthlyPeriods = data.periods;
    const allMonths = [...monthlyPeriods].sort(compareMonthYearHelper);
    let targetPeriods: string[] = [];
    if (viewType === 'quarterly') {
      targetPeriods = allMonths.filter(m => ['Mar', 'Jun', 'Sep', 'Dec'].includes(m.split('-')[0]));
    } else {
      // yearly
      targetPeriods = allMonths.filter(m => m.split('-')[0] === 'Dec');
    }
    // For each period, just use the value from that month (not a sum)
    const transformedLineItems: BSLineItem[] = data.line_items.map(item => {
      if (!item.values || item.type === 'section_header' || item.type === 'category_header') {
        return item;
      }
      const newValues: number[] = targetPeriods.map(period => {
        const monthIndex = monthlyPeriods.indexOf(period);
        return monthIndex !== -1 && item.values ? (item.values[monthIndex] || 0) : 0;
      });
      return { ...item, values: newValues };
    });
    return { periods: targetPeriods, line_items: transformedLineItems };
  }, [viewType]);

  const transformedData = transformDataForView(currentDataSource);
  const periods = transformedData?.periods || [];

  // Expand/collapse logic
  const expandAll = () => {
    if (!currentDataSource) {
      setPendingExpandAll(true);
      return;
    }
    const newCategories: Record<string, boolean> = {};
    currentDataSource.line_items
      .filter(item => item.type === 'category_header' && item.category_value)
      .forEach(item => {
        if (item.category_value) {
          newCategories[item.category_value] = true;
        }
      });
    setExpandedCategories(newCategories);
    const newSections: Record<string, boolean> = {};
    currentDataSource.line_items
      .filter(item => item.type === 'section_header' && item.section_id)
      .forEach(item => {
        if (item.section_id) {
          newSections[item.section_id] = true;
        }
      });
    setExpandedSections(newSections);
    setPendingExpandAll(false);
  };

  const collapseAll = () => {
    if (!currentDataSource) return;
    const newCategories: Record<string, boolean> = {};
    currentDataSource.line_items
      .filter(item => item.type === 'category_header' && item.category_value)
      .forEach(item => {
        if (item.category_value) {
          newCategories[item.category_value] = false;
        }
      });
    setExpandedCategories(newCategories);
    const newSections: Record<string, boolean> = {};
    currentDataSource.line_items
      .filter(item => item.type === 'section_header' && item.section_id)
      .forEach(item => {
        if (item.section_id) {
          newSections[item.section_id] = false;
        }
      });
    setExpandedSections(newSections);
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Get categories by type from active template
  const getCategoriesByType = (types: AccountType[]): string[] => {
    if (!activeTemplate) {
      return [];
    }

    return activeTemplate.categories
      .filter(cat => types.includes(cat.type))
      .map(cat => cat.value);
  };

  // Get the category display name from template
  const getCategoryDisplayName = (categoryValue: string): string => {
    if (!activeTemplate) return categoryValue;
    
    const templateCategory = activeTemplate.categories.find(cat => cat.value === categoryValue);
    return templateCategory?.label || categoryValue;
  };

  // Get category type based on active template
  const getCategoryType = (category: string): AccountType => {
    if (!activeTemplate) return 'n/a';
    
    const templateCategory = activeTemplate.categories.find(cat => cat.value === category);
    return templateCategory?.type || 'n/a';
  };

  // Get accounts for a specific category and month
  const getCategoryAccountsByMonth = (category: string, month: string) => {
    return accounts.filter(account => account.category === category && account.month === month);
  };

  // Calculate total for a category in a specific month
  const calculateMonthlyTotal = (category: string, month: string) => {
    const monthlyAccounts = getCategoryAccountsByMonth(category, month);
    const categoryType = getCategoryType(category);
    const isAsset = ['current-asset', 'non-current-asset'].includes(categoryType);
    const isContraAsset = ['contra-current-asset', 'contra-non-current-asset'].includes(categoryType);
    
    return monthlyAccounts.reduce((total, account) => {
      return total + ((isAsset || isContraAsset) 
        ? account.debit - account.credit 
        : account.credit - account.debit);
    }, 0);
  };

  // Calculate total for a section in a specific month
  const calculateSectionTotal = (types: AccountType[], month: string) => {
    const categories = getCategoriesByType(types); 
    return categories.reduce((total, category) => {
      return total + calculateMonthlyTotal(category, month);
    }, 0);
  };

  // Calculate totals for assets, liabilities, and equity
  const calculateTotalAssets = (month: string) => {
    return calculateSectionTotal(
      ['current-asset', 'contra-current-asset', 'non-current-asset', 'contra-non-current-asset'],
      month
    );
  };

  const calculateTotalLiabilities = (month: string) => {
    return calculateSectionTotal(
      ['current-liability', 'contra-current-liability', 'non-current-liability', 'contra-non-current-liability'],
      month
    );
  };

  // Net Income calculation functions 
  // Function to get all accounts for a specific month
  const getMonthAccounts = (month: string) => {
    return accounts.filter(account => account.month === month);
  };

  // Function to calculate net income for a specific month
  const calculateNetIncome = (month: string) => {
    const monthAccounts = getMonthAccounts(month);
    
    if (!activeTemplate) return 0;
    
    // Get revenue categories
    const revenueCategories = activeTemplate.categories
      .filter(cat => cat.type === 'revenue')
      .map(cat => cat.value);
    
    // Get contra-revenue categories
    const contraRevenueCategories = activeTemplate.categories
      .filter(cat => cat.type === 'contra-revenue')
      .map(cat => cat.value);
    
    // Get expense categories (all expense types)
    const expenseCategories = activeTemplate.categories
      .filter(cat => ['cogs', 'contra-cogs', 'operating-expense', 'interest-expense', 
                      'income-tax-expense', 'other-expense'].includes(cat.type))
      .map(cat => cat.value);
    
    // Calculate revenue
    const revenue = monthAccounts
      .filter(account => revenueCategories.includes(account.category))
      .reduce((total, account) => total + (account.credit - account.debit), 0);
    
    // Calculate contra-revenue
    const contraRevenue = monthAccounts
      .filter(account => contraRevenueCategories.includes(account.category))
      .reduce((total, account) => total + (account.debit - account.credit), 0);
    
    // Calculate expenses
    const expenses = monthAccounts
      .filter(account => expenseCategories.includes(account.category))
      .reduce((total, account) => total + (account.debit - account.credit), 0);
    
    // Net Income = Revenue - Contra Revenue - Expenses
    return revenue - contraRevenue - expenses;
  };

  const calculateTotalEquity = (month: string) => {
    return calculateSectionTotal(['equity', 'contra-equity'], month) + calculateNetIncome(month);
  };

  const calculateLiabilitiesAndEquityTotal = (month: string) => {
    return calculateTotalLiabilities(month) + calculateTotalEquity(month);
  };

  const calculateCheck = (month: string) => {
    const totalAssets = calculateTotalAssets(month);
    const totalLiabilitiesAndEquity = calculateLiabilitiesAndEquityTotal(month);
    return totalAssets - totalLiabilitiesAndEquity;
  };

  // UI rendering
  if (!activeTemplate) {
    return (
      <div className="p-8 text-center">
        <p className="text-lg text-gray-600">
          Please select an active template to view the detailed balance sheet.
        </p>
      </div>
    );
  }
  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading Balance Sheet data...</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-8 text-center">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">{error}</p>
        </div>
      </div>
    );
  }
  if (!transformedData) {
    return (
      <div className="p-8 text-center">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">No Balance Sheet data available.</p>
        </div>
      </div>
    );
  }

  // Rendering logic for backend-driven line_items
  return (
    <div className="overflow-hidden">
      <style>{`
        .financial-table {
          table-layout: auto;
        }
        
        .financial-table th:first-child,
        .financial-table td:first-child {
          width: 240px;
          min-width: 240px;
          max-width: 240px;
        }

        /* Account number column */
        .financial-table th:nth-child(2) {
          width: auto;
          min-width: 120px;
          max-width: 140px;
          white-space: normal;
          word-break: break-word;
          text-align: center;
        }
        .financial-table td:nth-child(2) {
          width: auto;
          min-width: 120px;
          max-width: 140px;
          white-space: nowrap;
          text-align: center;
        }
        
        .financial-table th:not(:first-child):not(:nth-child(2)),
        .financial-table td:not(:first-child):not(:nth-child(2)) {
          width: auto;
          min-width: 160px;
          max-width: 200px;
        }
    
        .financial-table th.value-column {
          width: auto;
          min-width: 160px;
          max-width: 200px;
        }
      `}</style>
      <div className="sticky top-0 z-50 border-b border-gray-200 mb-4">
        <div className="flex justify-end items-center space-x-4 p-4">
          <ViewToggle
            currentView={viewType}
            onViewChange={setViewType}
            projectPeriodType={selectedProject?.period_type}
          />
          <button
            onClick={expandAll}
            className={`inline-flex items-center px-3 py-2 border text-sm leading-4 font-medium rounded-md transition-colors ${
              Object.values(expandedCategories).every(Boolean)
                ? 'text-white bg-blue-600 border-transparent'
                : 'text-blue-700 bg-blue-100 border-blue-300 hover:bg-blue-200'
            }`}
          >
            <ChevronDownSquare className="h-4 w-4 mr-2" />
            Expand All
          </button>
          <button
            onClick={collapseAll}
            className={`inline-flex items-center px-3 py-2 border text-sm leading-4 font-medium rounded-md transition-colors ${
              Object.values(expandedCategories).every(v => !v)
                ? 'text-white bg-blue-600 border-transparent'
                : 'text-blue-700 bg-blue-100 border-blue-300 hover:bg-blue-200'
            }`}
          >
            <ChevronUpSquare className="h-4 w-4 mr-2" />
            Collapse All
          </button>
        </div>
      </div>
      {/* Template info */}
      <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center">
        <span className="mr-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
          Active Template:
        </span>
        <span className="text-sm text-blue-800 font-medium">
          {activeTemplate.name}
        </span>
      </div>
      {/* Table container */}
      <div className="overflow-hidden border border-gray-200 rounded-lg">
        <div className="border border-gray-200 rounded-lg">
          <div className="max-h-screen h-128 overflow-auto">
            <div className="inline-block min-w-fit">
              <table className="financial-table divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th className="sticky left-0 top-0 z-[110] px-6 py-3 text-left text-sm font-semibold text-black border-r border-b border-gray-200 bg-gray-50">Description</th>
                    <th className="sticky top-0 z-[100] px-6 py-3 text-left text-sm font-semibold text-black border-b border-gray-200 bg-gray-50">Account Number</th>
                    {periods.map(period => (
                      <th key={period} className="sticky top-0 z-[100] px-6 py-3 text-right text-sm font-semibold text-black border-b border-gray-200 value-column bg-gray-50">
                        {getDateForPeriod(period, accounts)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {transformedData.line_items.map((item, idx) => {
                    if (item.type === 'section_header') {
                      return (
                        <tr key={idx} className="bg-gray-50">
                          <td className="sticky left-0 z-10 bg-gray-50 px-6 py-3 text-sm font-semibold text-gray-900 border-r border-gray-200">
                            <div className="flex items-center">
                              <button onClick={() => item.section_id && toggleSection(item.section_id)} className="mr-2 focus:outline-none">
                                {expandedSections[item.section_id || ''] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                              </button>
                              {item.name}
                            </div>
                          </td>
                          <td className="px-6 py-3 text-sm font-semibold text-gray-900"></td>
                          {periods.map(period => (
                            <td key={period} className="px-6 py-3 text-sm font-semibold text-gray-900"></td>
                          ))}
                        </tr>
                      );
                    }
                    if (item.type === 'category_header') {
                      // Always render category headers, regardless of section expand/collapse
                      // Find the corresponding category_total for this category
                      const categoryTotal = transformedData.line_items.find(
                        (li, i) =>
                          li.type === 'category_total' &&
                          li.category === item.category_value &&
                          i > idx // must come after the header
                      );
                      return (
                        <tr key={idx} className="bg-gray-50 cursor-pointer hover:bg-gray-100" onClick={() => item.category_value && toggleCategory(item.category_value)}>
                          <td className="sticky left-0 z-10 bg-gray-50 px-6 py-4 text-sm text-gray-900 border-r border-gray-200">
                            <div className="flex items-center">
                              {item.category_value && (
                                expandedCategories[item.category_value] ? <ChevronDown className="h-4 w-4 mr-2" /> : <ChevronRight className="h-4 w-4 mr-2" />
                              )}
                              <span>{item.name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900"></td>
                          {periods.map((period, i) => (
                            <td key={period} className="px-6 py-4 text-sm text-gray-900 text-right">
                              {categoryTotal && categoryTotal.values && categoryTotal.values[i] !== undefined
                                ? categoryTotal.values[i].toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                                : ''}
                            </td>
                          ))}
                        </tr>
                      );
                    }
                    if (item.type === 'account') {
                      // Only render if parent category is expanded
                      if (!item.category || !expandedCategories[item.category]) return null;
                      return (
                        <tr key={idx} className="bg-gray-50">
                          <td className="sticky left-0 z-10 bg-gray-50 pl-12 py-2 text-sm text-gray-600 border-r border-gray-200">{item.name}</td>
                          <td className="px-6 py-2 text-sm text-gray-600">{item.account_number}</td>
                          {item.values && item.values.map((val, i) => (
                            <td key={i} className="px-6 py-2 text-sm text-gray-600 text-right">
                              {val.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                            </td>
                          ))}
                        </tr>
                      );
                    }
                    if (item.type === 'category_total') {
                      // Only render if parent category is expanded
                      if (!item.category || !expandedCategories[item.category]) return null;
                      return (
                        <tr key={idx} className="bg-gray-100">
                          <td className="sticky left-0 z-10 bg-gray-100 px-6 py-2 text-sm font-medium text-gray-900 border-r border-gray-200">{item.name}</td>
                          <td className="px-6 py-2 text-sm font-medium text-gray-900"></td>
                          {item.values && item.values.map((val, i) => (
                            <td key={i} className="px-6 py-2 text-sm font-medium text-gray-900 text-right">
                              {val.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                            </td>
                          ))}
                        </tr>
                      );
                    }
                    if (item.type === 'section_total' || item.type === 'major_total' || item.type === 'calculated_total') {
                      const bgClass = item.type === 'major_total' ? 'bg-gray-300' : item.type === 'calculated_total' ? 'bg-blue-100' : 'bg-gray-200';
                      return (
                        <tr key={idx} className={bgClass}>
                          <td className={`sticky left-0 z-10 ${bgClass} px-6 py-3 text-sm font-semibold text-gray-900 border-r border-gray-200`}>{item.name}</td>
                          <td className="px-6 py-3 text-sm font-semibold text-gray-900"></td>
                          {item.values && item.values.map((val, i) => (
                            <td key={i} className="px-6 py-3 text-sm font-semibold text-gray-900 text-right">
                              {val.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                            </td>
                          ))}
                        </tr>
                      );
                    }
                    if (item.type === 'balance_check') {
                      return (
                        <tr key={idx} className="border-t-2 border-gray-400">
                          <td className="sticky left-0 z-10 bg-gray-50 px-6 py-4 text-sm font-semibold text-gray-900 border-r border-gray-200">{item.name}</td>
                          <td className="px-6 py-4 text-sm font-semibold text-gray-900"></td>
                          {item.values && item.values.map((val, i) => {
                            const isBalanced = Math.abs(val) < 0.01;
                            return (
                              <td key={i} className={`px-6 py-4 text-sm font-semibold text-right ${isBalanced ? 'text-green-600' : 'text-red-600'}`}>
                                {val.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    }
                    return null;
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default UnifiedDetailedBalanceSheet;
